import { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import logging from "../utils/logging";
import { useAuth } from "./AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function getWsUrl() {
  const url = new URL(BACKEND_URL);
  const protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${url.host}/api/ws`;
}

/**
 * Singleton WebSocket context. ONE connection per browser tab; every consumer
 * (Dashboard live alerts, SanctionBanner, AdminAppealsPage, future panels)
 * registers a handler that gets called for every inbound message and decides
 * for itself whether to act on it.
 *
 * Why this matters: each previously-mounted component opened its own socket.
 * On a /dashboard view as a sanctioned admin we were keeping 3 sockets alive
 * — wasteful for the server, and a flake risk on flaky cellular networks
 * because every individual reconnect cycle could miss messages.
 *
 * The context auto-reconnects with a 5s backoff if the socket drops; handlers
 * survive across reconnects because they live in a Set, not on the socket.
 *
 * SEC-001 hardening: the socket is only opened when the current user is
 * authenticated. Anonymous routes (login, magic-link verify, public visitor
 * doorbell page) skip the connection entirely so we don't waste bandwidth on
 * a handshake that would be rejected with 4401. A 4401 close code from the
 * server (session invalidated mid-tab) suspends reconnects; the next auth
 * state change re-opens the socket automatically.
 */
const WebSocketContext = createContext({ connected: false, subscribe: () => () => {} });

// Custom close code emitted by the backend when the session cookie is
// missing/expired. Mirrors the HTTP 401 convention (4000-4999 is app-
// defined per the WebSocket spec).
const CLOSE_CODE_UNAUTH = 4401;

export function WebSocketProvider({ children }) {
  const [connected, setConnected] = useState(false);
  const { user, loading } = useAuth();
  const wsRef = useRef(null);
  const handlersRef = useRef(new Set());
  const reconnectTimer = useRef(null);
  const unmountedRef = useRef(false);
  const suspendedRef = useRef(false); // true after a 4401 close — waits for re-auth

  const connect = useCallback(() => {
    if (unmountedRef.current) return;
    if (suspendedRef.current) return;
    if (wsRef.current?.readyState === WebSocket.OPEN || wsRef.current?.readyState === WebSocket.CONNECTING) return;

    try {
      const ws = new WebSocket(getWsUrl());
      ws.onopen = () => {
        setConnected(true);
        logging.info("WebSocket connected");
      };
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          // Snapshot the handler set so a handler that unsubscribes during
          // dispatch (legal!) doesn't mutate the iteration.
          [...handlersRef.current].forEach((h) => {
            try { h(data); }
            catch (err) { logging.warn("WS handler threw", err); }
          });
        } catch (err) {
          logging.warn("WebSocket parse error", err);
        }
      };
      ws.onclose = (ev) => {
        setConnected(false);
        if (unmountedRef.current) return;
        if (ev && ev.code === CLOSE_CODE_UNAUTH) {
          // Server rejected the handshake — don't reconnect until the
          // auth state changes (see effect below).
          suspendedRef.current = true;
          logging.info("WebSocket rejected (unauth); waiting for sign-in");
          return;
        }
        logging.info("WebSocket disconnected, reconnecting...");
        reconnectTimer.current = setTimeout(connect, 5000);
      };
      ws.onerror = () => { ws.close(); };
      wsRef.current = ws;
    } catch (err) {
      logging.warn("WebSocket connection failed", err);
      reconnectTimer.current = setTimeout(connect, 5000);
    }
  }, []);

  useEffect(() => {
    unmountedRef.current = false;
    // Only open the socket for authenticated sessions. AuthContext's
    // `loading` flag holds the initial /auth/me round-trip; once it settles
    // and a user is present, we connect. On logout (user → null), we tear
    // the socket down.
    if (loading) return () => {};
    if (!user?.user_id) {
      // Not authenticated — make sure any lingering socket is closed and
      // no reconnect is queued.
      suspendedRef.current = false;
      clearTimeout(reconnectTimer.current);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
      setConnected(false);
      return () => {};
    }
    // Authenticated — clear any prior suspension and connect.
    suspendedRef.current = false;
    connect();
    return () => {
      unmountedRef.current = true;
      clearTimeout(reconnectTimer.current);
      if (wsRef.current) {
        wsRef.current.onclose = null; // prevent reconnect on unmount
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect, user?.user_id, loading]);

  const subscribe = useCallback((handler) => {
    if (typeof handler !== "function") return () => {};
    handlersRef.current.add(handler);
    return () => { handlersRef.current.delete(handler); };
  }, []);

  // Memoize the context value so consumers don't re-render on every parent
  // render — only when `connected` actually flips. `subscribe` is already
  // stable via useCallback.
  const value = useMemo(() => ({ connected, subscribe }), [connected, subscribe]);

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

/**
 * Backwards-compatible hook: same signature as the original `useWebSocket`,
 * but plumbed through the singleton. Returns `{connected}`. The handler is
 * read via a ref so subscribers don't have to wrap it in useCallback.
 */
export function useWebSocket(onMessage) {
  const ctx = useContext(WebSocketContext);
  const handlerRef = useRef(onMessage);
  handlerRef.current = onMessage;
  useEffect(() => {
    const unsubscribe = ctx.subscribe((msg) => {
      if (handlerRef.current) handlerRef.current(msg);
    });
    return unsubscribe;
  }, [ctx]);
  return { connected: ctx.connected };
}
